import React, { useState, useEffect } from 'react';
import {
  Box, Container, Paper, Typography,
  Chip, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Badge, Alert, Skeleton, Button, IconButton, Tooltip
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventIcon from '@mui/icons-material/Event';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import PrintIcon from '@mui/icons-material/Print';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const EmployeeHolidayView = () => {
  const [holidayList, setHolidayList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [companyId, setCompanyId] = useState('');

  const holidayTypes = {
    public: { label: 'Public Holiday', color: '#ef4444', bgColor: '#fef2f2',  },
    national: { label: 'National Holiday', color: '#3b82f6', bgColor: '#eff6ff', },
    religious: { label: 'Religious Holiday', color: '#a855f7', bgColor: '#faf5ff', },
    optional: { label: 'Optional Holiday', color: '#f59e0b', bgColor: '#fffbeb',},
    company: { label: 'Company Holiday', color: '#06b6d4', bgColor: '#ecfeff',}
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchCompanyId();
  }, []);

  useEffect(() => {
    if (companyId) {
      fetchHolidayList();
    }
  }, [companyId]);

  const fetchCompanyId = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCompanyId(payload.company_id);
      } else {
        setError('Authentication token not found. Please log in again.');
      }
    } catch (err) {
      //console.error('Error fetching company ID:', err);
      setError('Failed to authenticate. Please log in again.');
    }
  };

  const fetchHolidayList = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/holidays`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (res.data.holidayList) {
        setHolidayList(res.data.holidayList);
      } else {
        setHolidayList(null);
      }
    } catch (err) {
      //console.error('Error fetching holiday list:', err);
      const errorMsg = err.response?.data?.error || 'Failed to fetch holiday list';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const getHolidaysForDate = (day, month, year) => {
    if (!holidayList || !holidayList.holidays) return [];
    const holidays = JSON.parse(holidayList.holidays);
    return holidays.filter(h => {
      const holidayDate = new Date(h.date + 'T00:00:00');
      return holidayDate.getDate() === day &&
             holidayDate.getMonth() === month &&
             holidayDate.getFullYear() === year;
    });
  };

  const handlePrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const handleToday = () => {
    const today = new Date();
    setSelectedMonth(today.getMonth());
    setSelectedYear(today.getFullYear());
  };

  const handlePrint = () => {
    if (!holidayList || !holidayList.holidays) return;
    const holidays = JSON.parse(holidayList.holidays);
    
    const printWindow = window.open('', '_blank');
    const title = holidayList.title || `Company Holiday List ${holidayList.year}`;
    const year = holidayList.year;
    
    const rowsHtml = holidays.map((h, index) => {
      const date = new Date(h.date + 'T00:00:00');
      const formattedDate = date.toLocaleDateString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
      const dayOfWeek = date.toLocaleDateString(undefined, { weekday: 'long' });
      const typeLabel = holidayTypes[h.type]?.label || h.type || 'Public Holiday';
      const typeColor = holidayTypes[h.type]?.color || '#3b82f6';
      const typeBg = holidayTypes[h.type]?.bgColor || '#eff6ff';

      return `
        <tr>
          <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; text-align: center; font-size: 12px;">${index + 1}</td>
          <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-weight: 600; white-space: nowrap; font-size: 12px;">${formattedDate}</td>
          <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; white-space: nowrap; font-size: 12px;">${dayOfWeek}</td>
          <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #1e293b; font-size: 12px;">${h.name}</td>
          <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; white-space: nowrap; font-size: 12px;">
            <span style="display: inline-block; white-space: nowrap; padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; background-color: ${typeBg}; color: ${typeColor}; border: 1px solid ${typeColor}; text-align: center;">
              ${typeLabel}
            </span>
          </td>
          <td style="padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-size: 12px;">${h.description || '-'}</td>
        </tr>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 12mm 15mm;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              margin: 0;
              padding: 15px;
              color: #1e293b;
              background: #fff;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .header {
              text-align: center;
              padding-bottom: 12px;
              border-bottom: 3px solid #667eea;
              margin-bottom: 16px;
            }
            .header h1 {
              margin: 0 0 4px 0;
              color: #2d3748;
              font-size: 22px;
              font-weight: 700;
            }
            .header p {
              margin: 0;
              color: #718096;
              font-size: 13px;
            }
            .meta-bar {
              display: flex;
              justify-content: space-between;
              align-items: center;
              background-color: #f8fafc;
              padding: 8px 14px;
              border-radius: 6px;
              margin-bottom: 16px;
              font-size: 12px;
              color: #475569;
              border: 1px solid #e2e8f0;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
              margin-top: 5px;
            }
            th {
              background-color: #ebf8ff;
              color: #2b6cb0;
              font-weight: 700;
              text-align: left;
              padding: 10px 8px;
              border-bottom: 2px solid #bee3f8;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            th:first-child {
              text-align: center;
            }
            td {
              vertical-align: middle;
              word-wrap: break-word;
            }
            .footer {
              margin-top: 24px;
              text-align: center;
              font-size: 11px;
              color: #a0aec0;
              border-top: 1px solid #e2e8f0;
              padding-top: 12px;
            }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${title}</h1>
            <p>Official Holiday Calendar - Year ${year}</p>
          </div>
          <div class="meta-bar">
            <span><strong>Total Holidays:</strong> ${holidays.length}</span>
            <span><strong>Printed Date:</strong> ${new Date().toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 35px; text-align: center;">#</th>
                <th style="width: 110px; white-space: nowrap;">Date</th>
                <th style="width: 100px; white-space: nowrap;">Day</th>
                <th style="width: 170px;">Holiday Name</th>
                <th style="width: 140px; white-space: nowrap;">Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
          <div class="footer">
            <p>PulseHR - Organization Holiday Calendar</p>
          </div>
          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
    const today = new Date();
    const isCurrentMonth = today.getMonth() === selectedMonth && today.getFullYear() === selectedYear;
    
    const days = [];
    const totalCells = Math.ceil((daysInMonth + firstDay) / 7) * 7;

    // Empty cells before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <Box
          key={`empty-${i}`}
          sx={{
            aspectRatio: '1',
            minHeight: 100,
            border: '1px solid',
            borderColor: 'grey.100',
            bgcolor: 'grey.50'
          }}
        />
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedYear, selectedMonth, day);
      const dayHolidays = getHolidaysForDate(day, selectedMonth, selectedYear);
      const isToday = isCurrentMonth && day === today.getDate();
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const hasHoliday = dayHolidays.length > 0;

      days.push(
        <Tooltip
          key={day}
          title={
            dayHolidays.length > 0 ? (
              <Box>
                {dayHolidays.map((h, idx) => (
                  <Box key={idx} sx={{ mb: idx < dayHolidays.length - 1 ? 1 : 0 }}>
                    <Typography variant="caption" fontWeight={600}>
                      {holidayTypes[h.type]?.icon} {h.name}
                    </Typography>
                    {h.description && (
                      <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                        {h.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            ) : ''
          }
          arrow
          placement="top"
        >
          <Box
            sx={{
              aspectRatio: '1',
              minHeight: 100,
              border: '1px solid',
              borderColor: isToday ? 'primary.main' : hasHoliday ? 'error.200' : 'grey.200',
              borderWidth: isToday ? 2 : 1,
              bgcolor: hasHoliday ? 'error.50' : isWeekend ? 'grey.50' : 'white',
              p: 1,
              position: 'relative',
              transition: 'all 0.2s',
              cursor: hasHoliday ? 'pointer' : 'default',
              '&:hover': {
                bgcolor: hasHoliday ? 'error.100' : isWeekend ? 'grey.100' : 'grey.50',
                transform: hasHoliday ? 'scale(1.02)' : 'none',
                zIndex: hasHoliday ? 10 : 1,
                boxShadow: hasHoliday ? 3 : 0
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Badge
                badgeContent={dayHolidays.length}
                color="error"
                invisible={dayHolidays.length === 0}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    height: 16,
                    minWidth: 16,
                    padding: '0 4px'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: isToday ? '50%' : 1,
                    bgcolor: isToday ? 'primary.main' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={isToday ? 700 : isWeekend ? 600 : 400}
                    color={isToday ? 'white' : isWeekend ? 'text.secondary' : 'text.primary'}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {day}
                  </Typography>
                </Box>
              </Badge>
            </Box>

            <Box sx={{ mt: 1 }}>
              {dayHolidays.slice(0, 2).map((holiday, idx) => (
                <Chip
                  key={idx}
                  label={holiday.name}
                  size="small"
                  sx={{
                    width: '100%',
                    height: 22,
                    fontSize: '0.65rem',
                    mb: 0.5,
                    bgcolor: holidayTypes[holiday.type]?.bgColor,
                    color: holidayTypes[holiday.type]?.color,
                    border: '1px solid',
                    borderColor: holidayTypes[holiday.type]?.color,
                    fontWeight: 600,
                    '& .MuiChip-label': {
                      px: 0.75,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }
                  }}
                  icon={
                    <span style={{ fontSize: '0.75rem', marginLeft: 4 }}>
                      {holidayTypes[holiday.type]?.icon}
                    </span>
                  }
                />
              ))}
              {dayHolidays.length > 2 && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                    fontWeight: 600
                  }}
                >
                  +{dayHolidays.length - 2} more
                </Typography>
              )}
            </Box>
          </Box>
        </Tooltip>
      );
    }

    // Empty cells after month ends
    const remainingCells = totalCells - days.length;
    for (let i = 0; i < remainingCells; i++) {
      days.push(
        <Box
          key={`empty-end-${i}`}
          sx={{
            aspectRatio: '1',
            minHeight: 100,
            border: '1px solid',
            borderColor: 'grey.100',
            bgcolor: 'grey.50'
          }}
        />
      );
    }

    return (
      <Box>
        {/* Calendar Header */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            bgcolor: 'primary.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'primary.200'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <IconButton onClick={handlePrevMonth} size="small" sx={{ bgcolor: 'white' }}>
              <ChevronLeftIcon />
            </IconButton>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                {months[selectedMonth]} {selectedYear}
              </Typography>
              <IconButton onClick={handleToday} size="small" sx={{ bgcolor: 'white' }}>
                <TodayIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <IconButton onClick={handleNextMonth} size="small" sx={{ bgcolor: 'white' }}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Paper>

        {/* Legend */}
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {Object.entries(holidayTypes).map(([key, val]) => (
            <Chip
              key={key}
              label={val.label}
              size="small"
              icon={<span>{val.icon}</span>}
              sx={{
                bgcolor: val.bgColor,
                color: val.color,
                border: '1px solid',
                borderColor: val.color,
                fontWeight: 600
              }}
            />
          ))}
        </Box>

        {/* Day Headers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, mb: 0 }}>
          {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, idx) => (
            <Box
              key={day}
              sx={{
                p: 1.5,
                bgcolor: idx === 0 || idx === 6 ? 'error.50' : 'primary.50',
                border: '1px solid',
                borderColor: 'grey.200',
                textAlign: 'center'
              }}
            >
              <Typography
                variant="body2"
                fontWeight={700}
                color={idx === 0 || idx === 6 ? 'error.main' : 'primary.main'}
                sx={{ fontSize: '0.75rem' }}
              >
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0 }}>
          {days}
        </Box>

        {/* Footer Stats */}
        <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            <strong>
              {holidayList ? JSON.parse(holidayList.holidays).filter(h => {
                const d = new Date(h.date + 'T00:00:00');
                return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
              }).length : 0}
            </strong> holidays in {months[selectedMonth]} {selectedYear}
          </Typography>
        </Paper>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
  <Container maxWidth={false} sx={{ py: 4, width: '100%' }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Holiday Calendar
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              View your organization's holiday calendar for the year
            </Typography>
          </Box>
          {holidayList && (
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                '&:hover': { bgcolor: 'grey.100' },
                fontWeight: 600,
                px: 4
              }}
            >
              Print Calendar
            </Button>
          )}
        </Box>
      </Paper>

      {/* Holiday Display Section */}
      {holidayList ? (
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {/* Header */}
          <Box
            sx={{
              p: 3,
              bgcolor: 'success.50',
              borderBottom: '1px solid',
              borderColor: 'success.200'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CalendarMonthIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Chip
                    label={`YEAR ${holidayList.year}`}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 600, mb: 1 }}
                  />
                  <Typography variant="h5" fontWeight={700} color="text.primary">
                    {holidayList.title}
                  </Typography>
                 
                </Box>
              </Box>
            </Box>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total Holidays:</strong> {JSON.parse(holidayList.holidays).length}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* View Toggle */}
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'grey.200', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={viewMode === 'list' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setViewMode('list')}
                startIcon={<EventIcon />}
              >
                List View
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setViewMode('calendar')}
                startIcon={<CalendarMonthIcon />}
              >
                Calendar View
              </Button>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handlePrint}
              startIcon={<PrintIcon />}
            >
              Print
            </Button>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3 }}>
            {viewMode === 'list' ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Day</TableCell>
                      <TableCell>Holiday Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {JSON.parse(holidayList.holidays).map((holiday, index) => {
                      const date = new Date(holiday.date + 'T00:00:00');
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {date.toLocaleDateString(undefined, {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {date.toLocaleDateString(undefined, { weekday: 'long' })}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {holiday.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={holidayTypes[holiday.type]?.label}
                              size="small"
                              icon={<span>{holidayTypes[holiday.type]?.icon}</span>}
                              sx={{
                                bgcolor: holidayTypes[holiday.type]?.bgColor,
                                color: holidayTypes[holiday.type]?.color,
                                border: '1px solid',
                                borderColor: holidayTypes[holiday.type]?.color
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {holiday.description || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              renderCalendar()
            )}
          </Box>
        </Paper>
      ) : (
        <Paper
          elevation={2}
          sx={{
            p: 8,
            textAlign: 'center',
            borderRadius: 3,
            border: '2px dashed',
            borderColor: 'grey.300'
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: 40, color: 'grey.400' }} />
          </Box>
          <Typography variant="h5" fontWeight={600} gutterBottom color="text.secondary">
            No Holidays Available
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
            Your company has not set up a holiday calendar yet. Contact HR for updates.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default EmployeeHolidayView;